<template>
  <div>
    <div v-if="error" class="text-center pa-4">
      <v-alert type="error" prominent>
        <v-row align="center">
          <v-col class="grow">
            <Typography variant="h-6">Error Loading Data</Typography>
            <div>{{ error }}</div>
          </v-col>
          <v-col class="shrink">
            <v-btn @click="$emit('retry')" color="primary"> Retry </v-btn>
          </v-col>
        </v-row>
      </v-alert>
    </div>

    <v-data-table
      v-else
      :loading="loading"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :server-items-length="totalCount"
      item-key="claimId"
      show-expand
      :single-expand="true"
      id="claims-table"
      class="elevation-1"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      no-data-text="No claims found"
      loading-text="loading"
      :page="currentPage"
      must-sort
      @update:items-per-page="onItemsPerPageChange"
      @update:sort-by="onSortByChange"
      @update:sort-desc="onSortDescChange"
      @update:page="onPageChange"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, 100],

        'items-per-page-text': 'Rows per page:',
        'page-text': `{0}-{1} of {2}`,
        'show-current-page': true,
        'show-first-last-page': true,
      }"
    >
      <template v-slot:[`item.serialNumber`]="{ item }">
        {{ item.serialNumber !== null && item.serialNumber !== "null" ? item.serialNumber : "" }}
      </template>
      <template v-slot:[`item.salesDate`]="{ item }">
        {{ formatDate(item.salesDate) }}
      </template>

      <template v-slot:[`item.claimDate`]="{ item }">
        {{ formatDate(item.claimDate) }}
      </template>
      <template v-slot:[`item.file`]="{ item }">
        <FileAttachmentButton
          :attachments="item.FormAttachments"
          :has-additional-claimants="hasAdditionalClaimants(item)"
          button-color="brandCyan"
          @click.stop
        />
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-select
          :value="pendingStatusRows[item.claimId] || mapReviewStatusToLabel(item.reviewStatus)"
          :items="getStatusOptions(item)"
          dense
          hide-details
          outlined
          @change="onStatusSelect(item, $event)"
          :disabled="
            !!savedStatusRows[item.claimId] ||
            item.reviewStatus === 'Approved' ||
            item.reviewStatus === 'Needs More Info' ||
            item.reviewStatus === 'Denied'
          "
          style="width: 190px"
        />
      </template>

      <template v-slot:[`item.auditComment`]="{ item }">
        <div class="audit-comment-wrapper">
          <v-text-field
            v-model="item.auditComment"
            dense
            hide-details
            outlined
            placeholder="Enter comment"
            class="audit-comment-field"
            :disabled="
              (pendingStatusRows[item.claimId] || item.reviewStatus) === 'Pending' ||
              !!savedStatusRows[item.claimId] ||
              item.reviewStatus === 'Approved' ||
              item.reviewStatus === 'Denied' ||
              item.reviewStatus === 'Needs More Info'
            "
            :error="!!commentErrors[item.claimId]"
            :error-messages="commentErrors[item.claimId] ? ['Comment is required'] : []"
            @input="onAuditCommentChange(item, $event)"
          />
          <div
            class="comment-buttons"
            :class="{
              hidden:
                !changedAuditCommentRows[item.claimId] ||
                getCurrentStatusLabel(item) === 'Pending Audit',
            }"
            title="Comment actions"
          >
            <v-btn
              icon
              medium
              color="green"
              @click="onApproveClick(item)"
              title="Save Comment"
              class="comment-save-btn"
              :disabled="!!loadingRows[item.claimId]"
            >
              <i class="mdi mdi-check-circle"></i>
            </v-btn>
            <v-btn
              icon
              medium
              @click="onCommentClose(item)"
              title="Close Comment"
              class="comment-close-btn"
              :disabled="!!loadingRows[item.claimId]"
            >
              <i class="mdi mdi-close"></i>
            </v-btn>
          </div>
        </div>
      </template>

      <template #expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-row id="claims-expanded-row" class="pa-3" no-gutters>
            <v-col cols="12" md="4">
              <p>Job Title: {{ item.jobTitle || "N/A" }}</p>
              <p>Client Name: {{ item.clientName || "N/A" }}</p>
              <p v-if="hasAdditionalClaimants(item)">
                Additional Claimants:
                {{ formatAdditionalClaimants(item.claimDependencies.additionalClaimants) }}
              </p>
            </v-col>
            <v-col cols="12" md="4">
              <p>Product Category: {{ item.productCategory || "N/A" }}</p>
              <p>Product Model: {{ item.productModel || "N/A" }}</p>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
// Components
import Typography from "@/components/generic/Typography.vue";
import FileAttachmentButton from "@/components/forms/FileAttachmentButton.vue";
import moment from "moment";

export default {
  name: "AuditClaimsTable",
  components: {
    Typography,
    FileAttachmentButton,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    itemsPerPage: {
      type: Number,
      default: 20,
    },
    sortBy: {
      type: String,
      default: "claimDate",
    },
    sortDesc: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVuetifyLoaded: false,
      changedToApprovedRows: new Set(),
      changedAuditCommentRows: {},
      // I used it to store original audit comments for change detection. may change when hooked to API
      originalAuditComments: {},
      // I use it to track pending selections before save pressed
      pendingStatusRows: {},
      // Track rows where the selected status has been saved for disabling comments/status
      savedStatusRows: {},
      // Track comment validation errors
      commentErrors: {},
      // Track loading state for individual rows during API calls
      loadingRows: {},
      statusOptions: ["Pending Audit", "Needs More Info", "Approved", "Denied"],
      attachmentsItem: null,

      headers: [
        {
          text: "Claim ID",
          value: "claimId",
          sortable: false,
          filterable: true,
        },
        {
          text: "Form ID",
          value: "formId",
          sortable: false,
          filterable: true,
        },
        {
          text: "Submitter's Name",
          value: "submitterName",
          sortable: false,
          filterable: true,
        },
        {
          text: "Dealer Name",
          value: "dealerName",
          sortable: false,
          filterable: true,
        },
        {
          text: "Product ID",
          value: "productId",
          sortable: false,
          filterable: true,
        },
        {
          text: "SN",
          value: "serialNumber",
          sortable: false,
          filterable: true,
        },
        {
          text: "Sales Date",
          value: "salesDate",
          sortable: true,
          filterable: true,
        },
        {
          text: "Claim Date",
          value: "claimDate",
          sortable: true,
          filterable: true,
        },
        {
          text: "File",
          value: "file",
          sortable: false,
          filterable: true,
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
          filterable: false,
        },
        {
          text: "Audit Comment",
          value: "auditComment",
          sortable: false,
          filterable: true,
        },
        {
          text: "Details",
          value: "data-table-expand",
        },
      ],
    };
  },
  methods: {
    mapReviewStatusToLabel(statusCode) {
      if (statusCode === "Pending") return "Pending Audit";
      return statusCode;
    },

    onStatusSelect(item, tentativeStatus) {
      // TEMP: store tentative choice before save, may change when hooked to API
      this.$set(this.pendingStatusRows, item.claimId, tentativeStatus);

      // show Save/Close buttons only if not "Pending Audit"
      if (tentativeStatus !== "Pending Audit") {
        this.$set(this.changedAuditCommentRows, item.claimId, true);
      } else {
        // Hide buttons when "Pending Audit" is selected
        this.$delete(this.changedAuditCommentRows, item.claimId);
      }
    },

    getCurrentStatusLabel(item) {
      return this.pendingStatusRows[item.claimId] || this.mapReviewStatusToLabel(item.reviewStatus);
    },
    formatDate(dateValue) {
      if (!dateValue || !moment(dateValue).isValid()) return "";
      return moment(dateValue).format("MM/DD/YY");
    },
    formatAdditionalClaimants(additionalClaimants) {
      // Checking for array still, in case this method used somewhere else
      if (
        !additionalClaimants ||
        !Array.isArray(additionalClaimants) ||
        additionalClaimants.length === 0
      ) {
        return "";
      }
      return additionalClaimants.map((claimant) => claimant.name).join(", ");
    },
    hasAdditionalClaimants(item) {
      return !!(
        item.claimDependencies &&
        item.claimDependencies.additionalClaimants &&
        Array.isArray(item.claimDependencies.additionalClaimants) &&
        item.claimDependencies.additionalClaimants.length > 0
      );
    },
    onCommentClose(item) {
      this.$emit("reject-click", { claimId: item.claimId, item: item });

      this.$delete(this.changedAuditCommentRows, item.claimId);
      this.$delete && this.$delete(this.pendingStatusRows, item.claimId);

      // Clear the audit comment input
      item.auditComment = "";
    },

    onApproveClick(item) {
      // determine final status
      const finalStatus =
        this.pendingStatusRows[item.claimId] !== undefined
          ? this.pendingStatusRows[item.claimId]
          : item.reviewStatus;
      // TEMP: Don't submit on pending audit
      if (finalStatus === "Pending Audit") {
        this.$store.dispatch("showSnackbar", {
          text: "Please select a valid status before saving.",
          color: "error",
        });
        return;
      }

      const comment = (item.auditComment && item.auditComment.trim()) || "";

      // comment is REQUIRED for all statuses except 'Approved'
      if (finalStatus !== "Approved" && !comment) {
        this.$set(this.commentErrors, item.claimId, true);
        return;
      }

      // Clear any existing validation errors
      this.$delete(this.commentErrors, item.claimId);

      // Set loading state for this row
      this.$set(this.loadingRows, item.claimId, true);

      this.$emit("approve-click", {
        claimId: item.claimId,
        item: item,
        finalStatus: finalStatus,
      });
    },

    onApproveSuccess(item, finalStatus) {
      this.originalAuditComments[item.claimId] = item.auditComment || "";

      // apply finalized status to the item
      item.reviewStatus = finalStatus;

      this.$set(this.savedStatusRows, item.claimId, finalStatus);
      this.$delete(this.pendingStatusRows, item.claimId);

      // hide Save/Close buttons
      this.$delete(this.changedAuditCommentRows, item.claimId);

      // Clear loading state
      this.$delete(this.loadingRows, item.claimId);

      this.$store.dispatch("showSnackbar", {
        text: "Claim successfully saved",
        color: "success",
      });
    },

    onApproveError(item) {
      // Clear loading state
      this.$delete(this.loadingRows, item.claimId);

      this.$store.dispatch("showSnackbar", {
        text: "Failed to update claim",
        color: "error",
      });
    },
    onAuditCommentChange(item, value) {
      const orig = this.originalAuditComments[item.claimId] || "";
      if ((value || "") !== (orig || "")) {
        // set flag to display Save/Close buttons only if status is not "Pending Audit"
        if (this.getCurrentStatusLabel(item) !== "Pending Audit") {
          this.$set(this.changedAuditCommentRows, item.claimId, true);
        }
      } else {
        this.$delete(this.changedAuditCommentRows, item.claimId);
      }

      // Clear validation errors when user starts typing
      if (this.commentErrors[item.claimId]) {
        this.$delete(this.commentErrors, item.claimId);
      }
    },

    getStatusOptions() {
      return ["Pending Audit", "Needs More Info", "Approved", "Denied"];
    },

    onPageChange(page) {
      // Emit a named object so parent can reliably distinguish this event
      this.$emit("page-change", { page });
    },

    onItemsPerPageChange(itemsPerPage) {
      // Emit a named object so parent can reliably distinguish this event
      this.$emit("items-per-page-change", { itemsPerPage });
    },

    onSortByChange(sortBy) {
      this.$emit("sort-change", sortBy, this.sortDesc);
    },

    onSortDescChange(sortDesc) {
      this.$emit("sort-change", this.sortBy, sortDesc);
    },
  },

  async mounted() {
    this.isVuetifyLoaded = typeof this.$vuetify !== "undefined";
  },
};
</script>

<style scoped>
h2 {
  margin-bottom: 20px;
  color: #1976d2;
}
#claims-table >>> .v-data-table__expanded__content {
  box-shadow: none;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
  text-align: left;
}

#claims-table >>> .v-data-table__expanded__content td {
  padding-top: 0.25em;
}

#claims-table >>> .v-data-table-header th {
  background-color: #d1d1d1;
  color: #000 !important;
}

#claims-table >>> .v-data-table__expanded__row td {
  border-bottom: none !important;
}

#claims-table .audit-comment-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 278px;
  min-width: 278px;
  box-sizing: border-box;
}

#claims-table .audit-comment-field {
  width: 230px !important;
  min-width: 230px;
  flex: none;
}

#claims-table .comment-buttons {
  width: 40px;
  display: flex;
  flex-direction: column;
  gap: 0;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}

#claims-table .comment-buttons.hidden {
  visibility: hidden;
  pointer-events: none;
}

#claims-table .comment-save-btn {
  color: #4caf50;
  font-size: 1.5em;
}
#claims-table .comment-close-btn {
  margin-top: -0.5em;
  color: #b00020;
  font-size: 1.5em;
}

#claims-expanded-row p {
  margin: 0.2em 0;
}
</style>
